import { useHead } from '@vueuse/head'
import { Post } from '@/models/Cms'
import { FrameControl } from '@/models/FrameControl'

export function setPageMetaTags(
  title: string,
  description: string,
  imageUrl: string | null,
  url: string,
): void {
  const meta = [
    // Standard SEO meta
    { name: 'description', content: description },
    { name: 'robots', content: 'index,follow' },

    // Open Graph (for Facebook, LinkedIn, etc.)
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: url },

    // Twitter Card
    { name: 'twitter:card', content: imageUrl ? 'summary_large_image' : 'summary' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": title,
    "description": description,
    "url": url,
    ...(imageUrl && { "image": imageUrl }) // Conditionally add image if available
  };

  // Ensure there's only one JSON-LD script tag
  const existingJsonLdScript = document.querySelector('script[type="application/ld+json"]');
  if (existingJsonLdScript) {
    // If script already exists, update its content
    existingJsonLdScript.textContent = JSON.stringify(jsonLd);
  } else {
    // If script doesn't exist, create and append a new one
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);
  }

  // Update the page meta tags
  useHead({
    meta,
    link: [
      { rel: 'canonical', href: url }
    ]
  });
}

export function setPostMetaTags(post: Post, appConfigs: FrameControl): void {
  const title = post.attributes.title;
  const excerpt = post.attributes.description || post.attributes.content?.substring(0, 160) || '';
  const imageUrl = post.attributes.thumbnail?.data?.attributes?.formats?.large?.url ||
                   post.attributes.thumbnail?.data?.attributes?.url || '';
  const authorName = post.attributes.author?.data?.attributes?.name || '';
  const category = post.attributes.category?.data?.attributes?.name || '';
  const publishedAt = post.attributes.publishedAt || post.attributes.createdAt || new Date().toISOString();

  const meta = [
    // Standard Meta
    { name: 'description', content: excerpt },
    { name: 'author', content: authorName },
    { name: 'robots', content: 'index,follow' },

    // Open Graph (Facebook, LinkedIn, etc.)
    { property: 'og:title', content: title },
    { property: 'og:description', content: excerpt },
    { property: 'og:image', content: imageUrl },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: typeof window !== 'undefined' ? window.location.href : '' },
    
    // Article-specific Open Graph tags
    { property: 'article:published_time', content: publishedAt },
    { property: 'article:author', content: authorName },
    { property: 'article:tag', content: category },

    // Twitter Cards
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: excerpt },
    { name: 'twitter:image', content: imageUrl },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": excerpt,
    "author": {
      "@type": "Person",
      "name": authorName
    },
    "datePublished": publishedAt,
    "url": typeof window !== 'undefined' ? window.location.href : '',
    ...(imageUrl && { "image": imageUrl }), // Conditionally add image if available
    "publisher": {
      "@type": "Organization",
      "name": appConfigs.general.name,
    }
  };

  // Ensure there's only one JSON-LD script tag
  const existingJsonLdScript = document.querySelector('script[type="application/ld+json"]');
  if (existingJsonLdScript) {
    // If script already exists, update its content
    existingJsonLdScript.textContent = JSON.stringify(jsonLd);
  } else {
    // If script doesn't exist, create and append a new one
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);
  }

  // Update the page meta tags
  useHead({
    title: `${title} - ${appConfigs.general.name}`,
    meta,
    link: [
      { rel: 'canonical', href: typeof window !== 'undefined' ? window.location.href : '' }
    ]
  });
}

export function emitPrerenderEvent(): void {
    // 🔔 Signal pre-render is ready
    document.dispatchEvent(new Event('render-event'));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.prerenderReady = true; 
}

export function detectPrerenderedPage(): boolean {
  let isPrerendered = false;

  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has('invalidate_cache')) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/ban-ts-comment
      isPrerendered = (window as any).__PRERENDERING__ === true;
    }

    if (!isPrerendered) {
      const el = document.getElementById('initial-data');
      if (el) el.remove();
    }
  }

  return isPrerendered;
}

export function assignPrerenderData(data: {
  post?: Post;
  recentPosts?: Post[];
  popularPosts?: Post[];
}) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/ban-ts-comment
  (window as any).__PRERENDER_DATA__ = data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/ban-ts-comment
function getPrerenderJson(): any {
  const el = document.getElementById('initial-data');
  if (!el) return {};
  try {
    return JSON.parse(el.textContent || '{}');
  } catch {
    return {};
  }
}

export function readRecentPostsFromPrerender(): Post[] {
  return getPrerenderJson().recentPosts || [];
}

export function readPopularPostsFromPrerender(): Post[] {
  return getPrerenderJson().popularPosts || [];
}

export function readPostFromPrerender(): Post | null {
  return getPrerenderJson().post || null;
}
